import { useState, useMemo, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, Check, Search, Video, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CropEditor } from "@/components/prototype/CropEditor";
import { AUTO_MOVEMENT } from "@/lib/presetClassify";

function PresetCard({ preset, selected, onSelect, testid }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={testid}
      className={`relative flex flex-col overflow-hidden rounded-2xl border text-left transition-all ${
        selected ? "border-re-red ring-2 ring-re-red/30" : "border-re-line hover:border-re-red/40"
      }`}
    >
      {selected && (
        <span className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-re-red text-white">
          <Check className="h-4 w-4" />
        </span>
      )}
      <div className="relative aspect-video w-full bg-re-soft">
        {preset.sampleVideoUrl ? (
          <video src={preset.sampleVideoUrl} muted loop playsInline className="h-full w-full object-cover" onMouseEnter={(e) => e.target.play().catch(() => {})} onMouseLeave={(e) => e.target.pause()} />
        ) : preset.iconUrl ? (
          <img src={preset.iconUrl} alt="" className="h-full w-full object-contain p-4" />
        ) : (
          <div className="flex h-full items-center justify-center text-re-muted"><Video className="h-6 w-6" /></div>
        )}
      </div>
      <div className="p-3">
        <p className="font-heading text-sm font-semibold text-re-charcoal">{preset.name || preset.key}</p>
        {preset.description && <p className="mt-1 line-clamp-2 text-xs text-re-muted">{preset.description}</p>}
        {(preset.goodImageExampleUrl || preset.badImageExampleUrl) && (
          <div className="mt-2 flex gap-2">
            {preset.goodImageExampleUrl && <img src={preset.goodImageExampleUrl} alt="Goed" className="h-10 w-10 rounded border border-emerald-300 object-cover" />}
            {preset.badImageExampleUrl && <img src={preset.badImageExampleUrl} alt="Minder" className="h-10 w-10 rounded border border-red-300 object-cover" />}
          </div>
        )}
      </div>
    </button>
  );
}

export function PhotoEditor({ open, photos, index, classified, orientation, selectedIds = [], onClose, onNavigate, onApplyPreset, onSetCrop, initialTab = "movements" }) {
  const [tab, setTab] = useState(initialTab);
  const [query, setQuery] = useState("");
  const photo = photos[index];

  // Sync the active tab whenever the modal is (re)opened with a requested tab.
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  const effects = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return classified.effects;
    return classified.effects.filter((p) => `${p.key} ${p.name} ${p.description || ""}`.toLowerCase().includes(q));
  }, [query, classified.effects]);

  if (!open || !photo) return null;
  const value = photo.presetKey || null; // null => auto

  const apply = (val, scope) => onApplyPreset(val, scope, photo.imageId);

  return (
    <div className="fixed inset-0 z-50 flex bg-black/60" data-testid="photo-editor">
      <div className="ml-auto flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl md:w-[92%]">
        {/* header */}
        <div className="flex items-center justify-between border-b border-re-line px-5 py-3">
          <div className="flex items-center gap-3">
            <img src={photo.previewUrl} alt="" className="h-10 w-10 rounded-lg object-cover" />
            <div>
              <p className="font-heading text-sm font-semibold text-re-charcoal">Foto {index + 1} van {photos.length}</p>
              <p className="max-w-[240px] truncate text-xs text-re-muted">{photo.filename}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="editor-close"><X className="h-5 w-5" /></Button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto p-5">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4 grid w-full grid-cols-3">
              <TabsTrigger value="movements" data-testid="tab-movements">Camerabewegingen</TabsTrigger>
              <TabsTrigger value="effects" data-testid="tab-effects">Speciale effecten</TabsTrigger>
              <TabsTrigger value="crop" data-testid="tab-crop">Uitsnede</TabsTrigger>
            </TabsList>

            <TabsContent value="movements">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <PresetCard preset={AUTO_MOVEMENT} selected={value === null} onSelect={() => apply(null, "one")} testid="movement-auto" />
                {classified.movements.map((p) => (
                  <PresetCard key={p.key} preset={p} selected={value === p.key} onSelect={() => apply(p.key, "one")} testid={`movement-${p.key}`} />
                ))}
              </div>
              {classified.movements.length === 0 && (
                <p className="mt-3 rounded-lg bg-re-soft px-3 py-2 text-xs text-re-muted">
                  Er zijn momenteel geen losse camerabewegingen beschikbaar. Gebruik &quot;Automatische camerabeweging&quot;.
                </p>
              )}
            </TabsContent>

            <TabsContent value="effects">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-re-muted" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Zoek een effect…" data-testid="effects-search" className="pl-9 border-re-line" />
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {effects.map((p) => (
                  <PresetCard key={p.key} preset={p} selected={value === p.key} onSelect={() => apply(p.key, "one")} testid={`effect-${p.key}`} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="crop">
              <CropEditor src={photo.previewUrl} orientation={orientation} crop={photo.crop} onChange={(crop) => onSetCrop(photo.imageId, crop)} />
            </TabsContent>
          </Tabs>
        </div>

        {/* apply actions */}
        {tab !== "crop" && (
          <div className="flex flex-wrap items-center gap-2 border-t border-re-line px-5 py-3">
            <Button size="sm" onClick={() => apply(value, "all")} data-testid="apply-all" className="rounded-full bg-re-charcoal text-white hover:bg-re-ink">Toepassen op alle foto&apos;s</Button>
            <Button size="sm" variant="outline" disabled={selectedIds.length === 0} onClick={() => apply(value, "selected")} data-testid="apply-selected" className="rounded-full">Toepassen op geselecteerde ({selectedIds.length})</Button>
            <Button size="sm" variant="ghost" onClick={() => apply(null, "one")} data-testid="reset-auto" className="rounded-full text-re-muted"><RotateCcw className="mr-1.5 h-4 w-4" /> Reset naar automatisch</Button>
          </div>
        )}

        {/* footer nav */}
        <div className="flex items-center justify-between border-t border-re-line px-5 py-3">
          <Button variant="outline" size="sm" disabled={index === 0} onClick={() => onNavigate(index - 1)} data-testid="editor-prev" className="rounded-full"><ChevronLeft className="mr-1 h-4 w-4" /> Vorige</Button>
          <Button size="sm" onClick={onClose} data-testid="editor-save" className="rounded-full bg-re-red text-white hover:bg-re-deepred">Opslaan</Button>
          <Button variant="outline" size="sm" disabled={index === photos.length - 1} onClick={() => onNavigate(index + 1)} data-testid="editor-next" className="rounded-full">Volgende <ChevronRight className="ml-1 h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
